import { NotificationType } from '@api/READ_ONLY/notifications_api/Api';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import {
  VisitBillingSyncBatchCompletedNotificationType,
  VisitCompleteBatchCompletedNotificationType,
  VisitDeleteBatchCompletedNotificationType,
} from '../../types/notifications';
import { notificationSentAtFormattedDate } from '../../utils';

type VisitBatchNotificationCardProps = {
  notificationDetails:
    | VisitDeleteBatchCompletedNotificationType
    | VisitBillingSyncBatchCompletedNotificationType
    | VisitCompleteBatchCompletedNotificationType;
  onClick: () => void;
};

const VisitBatchNotificationCard = ({ notificationDetails, onClick }: VisitBatchNotificationCardProps) => {
  const { t } = useTranslation();
  const isDeleteBatch = notificationDetails.type === NotificationType.VisitDeleteBatchCompleted;
  const isBillingBatch = notificationDetails.type === NotificationType.VisitBillingSyncBatchCompleted;
  const isCompleteBatch = notificationDetails.type === NotificationType.VisitCompleteBatchCompleted;

  const deleteMeta = isDeleteBatch ? notificationDetails.metadata?.[0] : undefined;
  const billingSyncMeta = isBillingBatch ? notificationDetails.metadata?.[0] : undefined;
  const completeMeta = isCompleteBatch ? notificationDetails.metadata?.[0] : undefined;

  const iconWrapperClass = isDeleteBatch
    ? 'bg-amber-100 text-amber-700'
    : isCompleteBatch
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-cyan-100 text-cyan-700';

  const icon = isDeleteBatch ? (
    <FiTrash2 size={22} />
  ) : isCompleteBatch ? (
    <FiCheckCircle size={22} />
  ) : (
    <FiRefreshCw size={22} />
  );

  const tagColor = isDeleteBatch ? 'gold' : isCompleteBatch ? 'green' : 'cyan';
  const tagLabel = isDeleteBatch
    ? t('notifications-tag-delete-batch')
    : isCompleteBatch
      ? t('notifications-tag-complete-batch')
      : t('notifications-tag-update-billing');

  const summaryItems = isDeleteBatch
    ? [
        { label: t('notifications-batch-requested'), value: deleteMeta?.requestedCount ?? 0 },
        { label: t('notifications-batch-deleted'), value: deleteMeta?.deletedCount ?? 0 },
        { label: t('notifications-batch-not-found'), value: deleteMeta?.notFoundCount ?? 0 },
      ]
    : isCompleteBatch
      ? [
          { label: t('notifications-batch-requested'), value: completeMeta?.requestedCount ?? 0 },
          { label: t('notifications-batch-completed'), value: completeMeta?.completedCount ?? 0 },
          { label: t('notifications-batch-skipped'), value: completeMeta?.skippedCount ?? 0 },
          { label: t('notifications-batch-failed'), value: completeMeta?.failedCount ?? 0 },
          { label: t('notifications-batch-not-found'), value: completeMeta?.notFoundCount ?? 0 },
        ]
      : [
          { label: t('notifications-batch-requested'), value: billingSyncMeta?.requestedCount ?? 0 },
          { label: t('notifications-batch-synced'), value: billingSyncMeta?.syncedCount ?? 0 },
          { label: t('notifications-batch-skipped'), value: billingSyncMeta?.skippedCount ?? 0 },
          { label: t('notifications-batch-failed'), value: billingSyncMeta?.failedCount ?? 0 },
        ];

  return (
    <div
      onClick={onClick}
      className={`group relative flex gap-4 rounded-xl border p-6 transition-all hover:shadow-lg cursor-pointer ${
        notificationDetails.isRead ? 'border-gray-200 bg-white' : 'border-blue-200 bg-blue-50'
      }`}
    >
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${iconWrapperClass}`}>
        {icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="mb-1 text-lg font-semibold text-gray-900">{notificationDetails.title}</h3>
          </div>
          <Tag color={tagColor}>{tagLabel}</Tag>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {summaryItems.map((item) => (
            <div key={item.label} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              {item.label}: {item.value}
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
          <span>{notificationSentAtFormattedDate(notificationDetails.createdAt)}</span>
          {!notificationDetails.isRead && (
            <span className="rounded-full bg-blue-500 px-2 py-1 text-xs font-medium text-white">
              {t('notifications-new-badge')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitBatchNotificationCard;
